/**
 * Tutorial progress — client-only, localStorage-backed.
 *
 * MVP has no backend for onboarding progress (#122). We persist three things in
 * the browser profile (already per-user, so no extra namespacing needed):
 *
 *   1. COMPLETED — the set of finished tutorial ids (JSON string[]). A tour is
 *      "done" once the user reaches + acknowledges its final step.
 *   2. IN-PROGRESS — a map of tutorial id → the step the user was on when they
 *      exited partway (JSON Record<string, number>). Lets a re-opened tour
 *      RESUME where it left off, and a "restart" wipe it back to step 1.
 *   3. NUDGE-DISMISSED — a one-shot flag for the first-login "take the tour"
 *      nudge, so it shows at most once per browser profile.
 *
 * All reads/writes are guarded so SSR / private-mode / quota-exceeded never
 * throw into the render tree (tutorials are a non-critical nicety).
 */

const DONE_KEY = "divineiot:tutorials:done";
const PROGRESS_KEY = "divineiot:tutorials:progress";
const NUDGE_KEY = "divineiot:tutorials:nudge-dismissed";

// ──────────────────────────────────────────────────────────────────────
// Low-level guarded JSON storage helpers
// ──────────────────────────────────────────────────────────────────────

function readJson<T>(key: string, fallback: T, validate: (v: unknown) => v is T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return validate(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — progress is best-effort, never fatal */
  }
}

// ──────────────────────────────────────────────────────────────────────
// Completed tutorials
// ──────────────────────────────────────────────────────────────────────

/** Read the raw completed-id list, tolerating absent/corrupt storage. */
function readDone(): string[] {
  return readJson<string[]>(
    DONE_KEY,
    [],
    (v): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string"),
  );
}

/** All completed tutorial ids (deduped, order not significant). */
export function getDoneIds(): string[] {
  return readDone();
}

/** Whether a given tutorial has been completed. */
export function isDone(id: string): boolean {
  return readDone().includes(id);
}

/** Whether the user has completed at least one tutorial. */
export function hasCompletedAny(): boolean {
  return readDone().length > 0;
}

/**
 * Mark a tutorial complete (idempotent). Also clears any in-progress pointer
 * for it, since a finished tour shouldn't offer to "resume". Returns the new
 * completed set.
 */
export function markDone(id: string): string[] {
  clearProgress(id);
  const current = readDone();
  if (current.includes(id)) return current;
  const next = [...current, id];
  writeJson(DONE_KEY, next);
  return next;
}

/** Clear a single completion (used by replay/reset). */
export function clearDone(id: string): string[] {
  const next = readDone().filter((x) => x !== id);
  writeJson(DONE_KEY, next);
  return next;
}

// ──────────────────────────────────────────────────────────────────────
// In-progress (resume) pointer
// ──────────────────────────────────────────────────────────────────────

/** Read the id → stepIndex map of partially-completed tours. */
function readProgress(): Record<string, number> {
  return readJson<Record<string, number>>(
    PROGRESS_KEY,
    {},
    (v): v is Record<string, number> =>
      typeof v === "object" &&
      v !== null &&
      !Array.isArray(v) &&
      Object.values(v).every((n) => typeof n === "number"),
  );
}

/**
 * Persist the step a user is currently on for a tour, so re-opening it can
 * resume there. Step 0 is treated as "no meaningful progress" and clears the
 * pointer (resuming at the very start is the same as a fresh start).
 */
export function setProgress(id: string, stepIndex: number): void {
  const map = readProgress();
  if (stepIndex <= 0) {
    if (!(id in map)) return;
    delete map[id];
  } else {
    map[id] = stepIndex;
  }
  writeJson(PROGRESS_KEY, map);
}

/** Clear the in-progress pointer for a tour (on finish or explicit restart). */
export function clearProgress(id: string): void {
  const map = readProgress();
  if (!(id in map)) return;
  delete map[id];
  writeJson(PROGRESS_KEY, map);
}

/**
 * The step a tour should resume at, or 0 if there's no saved progress. Callers
 * clamp this against the live step count (a tour definition may have changed
 * length since the pointer was written).
 */
export function getResumeStep(id: string): number {
  const map = readProgress();
  const n = map[id];
  return typeof n === "number" && n > 0 ? n : 0;
}

/** Whether a tour has a mid-way resume pointer saved. */
export function hasProgress(id: string): boolean {
  return getResumeStep(id) > 0;
}

// ──────────────────────────────────────────────────────────────────────
// First-login nudge (one-shot)
// ──────────────────────────────────────────────────────────────────────

/** Whether the first-login "take the tour" nudge has been dismissed. */
export function isNudgeDismissed(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(NUDGE_KEY) === "1";
  } catch {
    return true;
  }
}

/** Permanently dismiss the first-login nudge (it shows at most once). */
export function dismissNudge(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NUDGE_KEY, "1");
  } catch {
    /* quota — ignore */
  }
}
